import React from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from './Avatar';
import EmployeeActionsMenu from './EmployeeActionsMenu';
import { employeeName, formatCurrency, getRole } from '../helpers';
import { PRIVATE_MASK } from '../constants';
import {
  TD, TEXT_INK, TEXT_PRIMARY, TEXT_TERTIARY,
  WARN_TEXT, WARN_SOFT, SUCCESS_TEXT, SUCCESS_SOFT, BRAND_DARK,
} from '../styles';
import type { Employee } from '../types';

interface Props {
  emp: Employee;
  privacy: boolean;
  isMenuOpen: boolean;
  onMenuToggle: (id: string | number | null) => void;
}

const notSet = (
  <span style={{ color: TEXT_TERTIARY, fontStyle: 'italic', fontSize: 13 }}>Not set</span>
);

export default function EmployeeRow({ emp, privacy, isMenuOpen, onMenuToggle }: Props) {
  const navigate = useNavigate();
  const role = getRole(emp);

  const renderPayRate = (): React.ReactNode => {
    if (privacy) return PRIVATE_MASK;
    if (emp.pay_type === 'hourly' && parseFloat(String(emp.hourly_rate))) {
      return <span style={{ fontSize: 13.5, color: TEXT_PRIMARY }}>{formatCurrency(emp.hourly_rate, emp.currency)}/hour</span>;
    }
    if (emp.pay_type === 'salary' && parseFloat(String(emp.salary_amount))) {
      return <span style={{ fontSize: 13.5, color: TEXT_PRIMARY }}>{formatCurrency(emp.salary_amount, emp.currency)}</span>;
    }
    return notSet;
  };

  const renderMethod = (): React.ReactNode => {
    const at = emp.account_type;
    if (at === 'direct_deposit') return <span style={{ fontSize: 13.5, color: TEXT_PRIMARY }}>Direct deposit</span>;
    if (at === 'cheque') return <span style={{ fontSize: 13.5, color: TEXT_PRIMARY }}>Cheque</span>;
    return notSet;
  };

  const renderVacation = (): React.ReactNode => {
    if (privacy) return PRIVATE_MASK;
    const ti = emp.tax_info || {};
    const balance = ti.balance_hours;
    if (balance != null && balance !== '') {
      return <span style={{ fontSize: 13.5, color: TEXT_PRIMARY }}>{Number(balance).toLocaleString()} hours</span>;
    }
    return notSet;
  };

  const renderEmail = (): React.ReactNode => {
    const email = emp.personal_email || emp.email || '';
    if (!email) return notSet;
    return (
      <a
        href={'mailto:' + email}
        style={{ color: BRAND_DARK, fontSize: 13.5, textDecoration: 'none' }}
        onClick={(e) => e.stopPropagation()}
      >{email}</a>
    );
  };

  const renderJobTitle = (): React.ReactNode => {
    const t = emp.position_title || emp.job_title || '';
    if (!t) return notSet;
    return <span style={{ fontSize: 13.5, color: TEXT_PRIMARY }}>{t}</span>;
  };

  const renderPhone = (): React.ReactNode => {
    const p = emp.phone;
    if (p) return <span style={{ fontSize: 13.5, color: TEXT_PRIMARY }}>{p}</span>;
    return notSet;
  };

  const renderStatus = (): React.ReactNode => {
    if (emp._ready) {
      return (
        <span style={{
          fontSize: 12, fontWeight: 600, color: SUCCESS_TEXT, background: SUCCESS_SOFT,
          padding: '3px 10px', borderRadius: 20,
        }}>Active</span>
      );
    }
    return (
      <span style={{
        fontSize: 12, fontWeight: 600, color: WARN_TEXT, background: WARN_SOFT,
        padding: '3px 10px', borderRadius: 20,
      }}>Needs setup</span>
    );
  };

  return (
    <tr>
      <td style={{ ...TD, paddingLeft: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar emp={emp} />
          <div style={{ minWidth: 0 }}>
            <div
              style={{ fontWeight: 600, color: TEXT_INK, fontSize: 14, cursor: 'pointer' }}
              onClick={() => navigate('/payroll/employees/' + emp.id)}
            >
              {employeeName(emp)}
            </div>
            <div style={{ fontSize: 12, color: role ? TEXT_TERTIARY : WARN_TEXT, marginTop: 2 }}>
              {role || 'Role not set'}
            </div>
          </div>
        </div>
      </td>
      <td style={TD}>{renderPayRate()}</td>
      <td style={TD}>{renderMethod()}</td>
      <td style={TD}>{renderVacation()}</td>
      <td style={TD}>{renderEmail()}</td>
      <td style={TD}>{renderJobTitle()}</td>
      <td style={TD}>{renderPhone()}</td>
      <td style={TD}>{renderStatus()}</td>
      <td style={{ ...TD, textAlign: 'center', width: 54 }}>
        <EmployeeActionsMenu emp={emp} isOpen={isMenuOpen} onToggle={onMenuToggle} />
      </td>
    </tr>
  );
}